<template>
  <div>
    <v-dialog
      :value="isDialogOpen"
      @input="closeDialog"
      max-width="480"
      persistent
      scrollable
    >
      <modal-card
        title="Update Outreach Status"
        padding
        full
        :loading="saving"
        @close="closeDialog"
      >
        <field-block
          :title="
            showResultActions || showFollowUpActions ? 'What\'s next?' : ''
          "
        >
          <v-list class="outreach-list">
            <v-list-item
              v-for="(action, idx) in actionsList"
              :key="idx"
              class="list-field__item"
              @click="() => action.action()"
            >
              {{ action.title }}
            </v-list-item>
          </v-list>
        </field-block>
      </modal-card>
    </v-dialog>
    <ScheduleCallbackDialog
      v-model="showCallbackDialog"
      :contact-id="callbackContactId"
      @saved="onCallbackSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/factories/eventBus';
import { RECRUITING_STATUS } from '../recruitingConstants';
import ScheduleCallbackDialog from './ScheduleCallbackDialog.vue';

export default {
  name: 'RecruitingOutreachDialog',
  components: { ScheduleCallbackDialog },
  data() {
    return {
      isDialogOpen: false,
      contactId: null,
      type: null,
      via3way: false,
      saving: false,
      showResultActions: false,
      showFollowUpActions: false,
      hideCallAgain: false,
      showCallbackDialog: false,
      callbackContactId: null,
    };
  },
  props: {
    single: Boolean,
  },
  computed: {
    ...mapGetters('fields', ['getSingleField']),
    ...mapGetters('users', ['currentUser']),
    ...mapGetters(['singleContact']),
    isCall() {
      return this.type === 'call';
    },

    actions() {
      const contactText = this.isCall
        ? 'Yes, I did a call'
        : 'Yes, I sent a text';

      return [
        {
          title: contactText,
          action: this.saveOutreachAttempt,
        },
        {
          title: "Didn't attempt yet",
          action: this.closeDialog,
        },
      ];
    },

    resultActions() {
      return [
        {
          title: RECRUITING_STATUS.NOT_INTERESTED,
          action: this.markAsNotInterested,
        },
        {
          title: this.isCall ? "Didn't answer" : 'Nothing yet',
          action: () => {
            this.showFollowUpActions = true;
            this.showResultActions = false;
          },
        },
        {
          title: 'Schedule Interview',
          action: this.openAppointmentDialog,
        },
        {
          title: 'Schedule Callback',
          action: this.openCallbackDialog,
        },
        {
          title: this.single ? 'Cancel' : 'Next Contact',
          action: this.updateLastContacted,
        },
      ];
    },

    followUpActions() {
      if (this.isCall) {
        const actions = [];

        if (!this.hideCallAgain) {
          actions.push({
            title: 'Call Again',
            action: () => this.emitFollowUpAction('callAgain'),
          });
        }

        actions.push(
          {
            title: 'Send Follow-Up Snippet',
            action: () => this.emitFollowUpAction('openSnippetsPicker'),
          },
          {
            title: 'Send Custom Message',
            action: () => this.emitFollowUpAction('openTextConversation'),
          },
          {
            title: 'Schedule Callback',
            action: this.openCallbackDialog,
          },
          {
            title: this.single ? 'Cancel' : 'Next Contact',
            action: this.updateLastContacted,
          }
        );

        return actions;
      } else {
        return [
          {
            title: 'Make a Call',
            action: () => this.emitFollowUpAction('makeCall'),
          },
          {
            title: this.single ? 'Cancel' : 'Next Contact',
            action: this.updateLastContacted,
          },
        ];
      }
    },

    actionsList() {
      if (this.showFollowUpActions) {
        return this.followUpActions;
      }

      if (this.showResultActions) {
        return this.resultActions;
      }

      return this.actions;
    },

    contactName() {
      const { first_name, last_name, name } = this.contact || {};
      if (name) return name;
      const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();
      return fullName || 'Unknown Contact';
    },

    contact() {
      return this.singleContact(this.contactId);
    },
  },
  methods: {
    ...mapActions(['updateContact', 'sendContactEvent']),

    async recordOutreach() {
      const countField = `total_${this.type}s`;
      const countFieldValue = (this.contact?.[countField] || 0) + 1;

      await Promise.all([
        this.recordEvent(this.type),
        this.updateField(countField, countFieldValue),
      ]);
    },

    async saveOutreachEvent() {
      try {
        this.saving = true;
        await Promise.all([
          this.recordOutreach(),
          this.updateStatusAfterOutreach(),
        ]);
      } finally {
        this.saving = false;
      }
    },

    async updateStatusAfterOutreach() {
      const { status, total_calls = 0, total_texts = 0 } = this.contact || {};

      // Skip if status already final
      if (
        [
          RECRUITING_STATUS.NOT_INTERESTED,
          RECRUITING_STATUS.REACHED,
          RECRUITING_STATUS.NOT_QUALIFIED,
        ].includes(status)
      ) {
        return;
      }

      const total = Number(total_calls) + Number(total_texts) + 1;

      // Only update if under limit
      const newStatus = total < 7 ? `LM${total}` : RECRUITING_STATUS.LM6;
      await this.updateStatus(newStatus);
    },

    async saveOutreachAttempt() {
      try {
        await this.saveOutreachEvent();
      } finally {
        this.showResultActions = true;
      }
    },

    emitFollowUpAction(action) {
      this.$emit(action);
      this.closeDialog();
    },

    async updateLastContacted() {
      try {
        this.saving = true;
        await this.updateField('last_contacted', new Date());
      } finally {
        this.closeDialog();
        this.saving = false;
      }
    },

    async updateStatus(status) {
      if (!Object.values(RECRUITING_STATUS).includes(status)) {
        return;
      }
      return await this.updateField('status', status);
    },

    async markAsNotInterested() {
      try {
        this.saving = true;
        await Promise.all([
          this.updateField('last_contacted', new Date()),
          this.updateStatus(RECRUITING_STATUS.NOT_INTERESTED),
        ]);
      } finally {
        this.saving = false;
        this.closeDialog();
      }
    },

    async updateField(fieldName, value) {
      const field = this.getSingleField(fieldName);
      const { name, bucketId } = field;
      const params = {
        [bucketId]: {
          [name]: value,
        },
      };

      return await this.updateContact({
        contactId: this.contactId,
        params,
        skipRefetch: fieldName !== 'last_contacted',
      });
    },

    async recordEvent() {
      const event_type = this.isCall ? 'received_call' : 'received_sms';

      const properties = {
        timestamp: Math.floor(Date.now() / 1000),
        contactId: this.contactId,
        userId: this.currentUser.id,
      };

      if (this.via3way) {
        properties.via = '3way';
      }

      return await this.sendContactEvent({
        contactId: this.contactId,
        params: {
          event_type,
          properties,
        },
      });
    },

    openAppointmentDialog() {
      EventBus.$emit('showAppointmentDialog', {
        contactId: this.contactId,
      });

      this.closeDialog();
    },

    openCallbackDialog() {
      this.callbackContactId = this.contactId;
      this.showCallbackDialog = true;
      this.closeDialog();
    },

    async onCallbackSaved({ contactId }) {
      const field = this.getSingleField('last_contacted');

      if (!field) return;

      const { name, bucketId } = field;
      await this.updateContact({
        contactId,
        params: { [bucketId]: { [name]: new Date() } },
        skipRefetch: false,
      });
    },

    openDialog({ contactId, type, via3way = false, hideCallAgain = false }) {
      this.contactId = contactId;
      this.type = type;
      this.via3way = via3way;
      this.hideCallAgain = hideCallAgain;
      this.isDialogOpen = true;
    },

    closeDialog() {
      this.isDialogOpen = false;
      this.contactId = null;
      this.type = null;
      this.via3way = false;
      this.showResultActions = false;
      this.showFollowUpActions = false;
      this.hideCallAgain = false;
    },
  },
  mounted() {
    EventBus.$on('showOutreachDialog', this.openDialog);
  },
  beforeDestroy() {
    EventBus.$off('showOutreachDialog', this.openDialog);
  },
};
</script>

<style lang="less">
.contact-info {
  .text-body-2 {
    display: flex;
    flex-direction: column;
  }
}

.outreach-list {
  border-radius: @base-radius !important;
  box-shadow: @base-shadow !important;
  border: 1px solid #eee !important;
  padding: 0 !important;
  margin: 12px 0;

  div.v-list-item {
    min-height: 28px;

    & + div.v-list-item {
      border-top: 1px solid #eee;
      margin-top: 0;
    }
  }
}
</style>
